import { createContext, useContext, useMemo } from "react";
import { config } from "../config";
import { useAuth } from "./AuthContext";

// One SPA, two storefronts. The site mode tells pricing, availability and
// header logic which behavior to use.
//
//   public — mysteryranch.com style: anonymous browsing, MSRP pricing,
//            public stock indicator (item-level qty), no auth required.
//   b2b    — b2b.mysteryranch.com: auth-gated browse + checkout, customer
//            price level, B2B custom-record availability.
//   dual   — single deployment (same domain) that flips to b2b once the
//            user authenticates with a real B2B price level.
//
// Effective mode is what the rest of the app reads. In `dual`, an
// authenticated user with a non-base price level is treated as B2B.

const SiteModeContext = createContext(null);

export function SiteModeProvider({ children }) {
  const { user } = useAuth();
  const value = useMemo(() => {
    const baseMode = config.siteMode;
    let effective = baseMode;
    if (baseMode === "dual") {
      effective = user?.priceLevel && user.priceLevel !== "base" ? "b2b" : "public";
    }
    return {
      siteMode: baseMode,
      effectiveMode: effective,
      isPublic: effective === "public",
      isB2B: effective === "b2b",
      // Where to send users who want the *other* experience. Empty string
      // means there is no separate site configured (single-domain dual).
      crossSiteUrl: effective === "b2b" ? config.publicSiteUrl : config.b2bSiteUrl,
    };
  }, [user]);
  return <SiteModeContext.Provider value={value}>{children}</SiteModeContext.Provider>;
}

export function useSiteMode() {
  const ctx = useContext(SiteModeContext);
  if (!ctx) throw new Error("useSiteMode must be used within SiteModeProvider");
  return ctx;
}
